import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { Sparkles, Check } from "lucide-react";
import { toast } from "sonner";

export default function Pricing() {
  const navigate = useNavigate();
  const [pricing, setPricing] = useState(null);
  const [me, setMe] = useState(null);
  const [billing, setBilling] = useState(null);
  const [loading, setLoading] = useState(null);

  useEffect(() => {
    api.get("/billing/pricing").then(({ data }) => setPricing(data));
    api
      .get("/auth/me")
      .then(({ data }) => setMe(data))
      .catch(() => setMe(false));
    api
      .get("/billing/me")
      .then(({ data }) => setBilling(data))
      .catch(() => setBilling(null));
  }, []);

  const checkout = async (passKey) => {
    if (!me) {
      navigate("/register");
      return;
    }
    setLoading(passKey);
    try {
      const { data } = await api.post("/billing/checkout", {
        pass_key: passKey,
        origin_url: window.location.origin,
      });
      if (data?.url) {
        window.location.href = data.url;
      } else {
        toast.error("Could not start checkout");
        setLoading(null);
      }
    } catch (err) {
      toast.error(formatApiError(err));
      setLoading(null);
    }
  };

  if (!pricing) {
    return (
      <div className="min-h-screen bg-[#FBF9F6]">
        <Navbar />
        <div className="max-w-4xl mx-auto px-4 py-24 text-center text-[#9A938A]">
          Loading…
        </div>
      </div>
    );
  }

  return (
    <div data-testid="pricing-page" className="min-h-screen bg-[#FBF9F6]">
      <Navbar />
      <main className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-12 sm:py-20">
        {/* Header */}
        <div className="text-center mb-12 sm:mb-16">
          <p className="text-xs uppercase tracking-[0.3em] text-[#9A938A] mb-3">
            Colleague+
          </p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl text-[#2D2926] tracking-tight">
            One simple price.
          </h1>
          <p className="mt-4 text-base text-[#6B655D] max-w-xl mx-auto leading-relaxed">
            {pricing.trial_days} days free for every new account. Then $9.99/month or
            $99/year for Colleague+ — Myra, Theo, Grace, Sam, Tyler,
            Avi, plus Emma & Ryan, voice notes, the lot.
          </p>
          {billing?.status === "active" && (
            <p
              data-testid="pricing-current-status"
              className="mt-4 text-xs uppercase tracking-wider text-[#D4A574]"
            >
              You're on Colleague+ — thank you ◇
            </p>
          )}
          {billing?.in_trial && billing?.days_remaining != null && (
            <p
              data-testid="pricing-trial-status"
              className="mt-4 text-xs uppercase tracking-wider text-[#9A938A]"
            >
              {billing.days_remaining} day{billing.days_remaining === 1 ? "" : "s"}{" "}
              left in your free trial
            </p>
          )}
        </div>

        {/* Two price cards */}
        <div className="grid sm:grid-cols-2 gap-5 sm:gap-6">
          {/* Monthly */}
          <div
            data-testid="pricing-card-monthly"
            className="bg-white rounded-3xl border border-[#E6E2D8] p-7 sm:p-8 flex flex-col"
          >
            <div className="text-xs uppercase tracking-[0.25em] text-[#9A938A]">
              Monthly
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-serif text-5xl text-[#2D2926]">$9.99</span>
              <span className="text-sm text-[#6B655D]">/ month</span>
            </div>
            <p className="mt-2 text-sm text-[#6B655D]">
              The flexible choice. Pay each month, pause anytime.
            </p>
            <button
              type="button"
              data-testid="pricing-monthly-btn"
              onClick={() => checkout("monthly")}
              disabled={!!loading}
              className="mt-6 bg-[#2D2926] text-white hover:bg-[#1A1715] rounded-full px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60"
            >
              {loading === "monthly" ? "Starting checkout…" : "Get monthly"}
            </button>
          </div>

          {/* Yearly */}
          <div
            data-testid="pricing-card-yearly"
            className="relative bg-gradient-to-br from-[#FBF6EE] to-white rounded-3xl border-2 border-[#D4A574] p-7 sm:p-8 flex flex-col"
          >
            <span className="absolute -top-3 left-7 bg-[#D4A574] text-white text-[10px] uppercase tracking-wider px-2.5 py-1 rounded-full">
              Save 16%
            </span>
            <div className="text-xs uppercase tracking-[0.25em] text-[#9A938A]">
              Yearly
            </div>
            <div className="mt-3 flex items-baseline gap-1">
              <span className="font-serif text-5xl text-[#2D2926]">$99</span>
              <span className="text-sm text-[#6B655D]">/ year</span>
            </div>
            <p className="mt-2 text-sm text-[#6B655D]">
              Two months free. The friendliest way to commit.
            </p>
            <button
              type="button"
              data-testid="pricing-yearly-btn"
              onClick={() => checkout("yearly")}
              disabled={!!loading}
              className="mt-6 bg-[#D4A574] text-[#2D2926] hover:bg-[#C99560] rounded-full px-6 py-3 text-sm font-medium transition-colors disabled:opacity-60"
            >
              {loading === "yearly" ? "Starting checkout…" : "Get yearly"}
            </button>
          </div>
        </div>

        {/* Features */}
        <div className="mt-14 sm:mt-16">
          <h3 className="font-serif text-2xl text-[#2D2926] mb-5 flex items-center gap-2">
            <Sparkles size={18} strokeWidth={1.5} className="text-[#D4A574]" />
            What's included
          </h3>
          <ul className="grid sm:grid-cols-2 gap-x-8 gap-y-3">
            {pricing.features_unlocked.map((f, i) => (
              <li
                key={i}
                className="flex items-start gap-2 text-sm text-[#3D3833]"
              >
                <Check
                  size={16}
                  strokeWidth={2}
                  className="text-[#D4A574] mt-0.5 flex-shrink-0"
                />
                <span>{f}</span>
              </li>
            ))}
          </ul>
        </div>

        <p className="mt-12 text-xs text-[#9A938A] text-center max-w-md mx-auto leading-relaxed">
          Tax included where applicable. Cancel anytime. If you don't renew,
          your conversations stay safe in your account — pay again whenever and
          pick up right where you left off.
        </p>
      </main>
      <Footer />
    </div>
  );
}
